import { useRef, useState } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { PolicyAction, PolicyFormValues } from '../policy-dialog';
import GroupsAutocomplete from '../../../shared/groups-autocomplete';
import z from 'zod';
import { profileFieldOptions } from '../../../shared/shared-profile-field-filter';

import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Delete from '@mui/icons-material/Delete';

interface DynamicGroupAssignmentProps {
  setValue: UseFormSetValue<PolicyFormValues>;
  control: Control<PolicyFormValues>;
  watch: UseFormWatch<PolicyFormValues>;
  errors: FieldErrors<PolicyFormValues>;
  trigger: UseFormTrigger<PolicyFormValues>;
}
export const DynamicGroupAssignmentModelSchema = z
  .object({
    profileFieldType: z.string().min(1, 'Profile Field Type is required.'),
    profileField: z.string().optional(),
    regEx: z.string().optional(),
    exact: z.boolean(),
    groupEmail: z.array(z.string()).min(1, 'Group is required.'),
    groupRole: z.array(z.string()),
  })
  .refine(
    (data) => {
      return (
        (data.profileField && data.profileField.trim() !== '') ||
        (data.regEx && data.regEx.trim() !== '')
      );
    },
    {
      message: 'Field Value or a Regular Expression is required.',
      path: ['profileField'],
    }
  );

export const DynamicGroupAssignmentActionSchema = z.object({
  type: z.literal('Dynamic_Group_Assignment'),
  dynamicGroupAssignment: DynamicGroupAssignmentModelSchema,
});

export type DynamicGroupAssignmentActionType = z.infer<
  typeof DynamicGroupAssignmentActionSchema
>;

// Type guard to check if an action is DynamicGroupAssignmentActionType
function isDynamicGroupAssignment(
  action: PolicyAction
): action is DynamicGroupAssignmentActionType {
  return action.type === 'Dynamic_Group_Assignment';
}

export default function DynamicGroupAssignment({
  setValue,
  control,
  watch,
  errors,
  trigger,
}: DynamicGroupAssignmentProps) {
  const actionData = watch('actionData') ?? [];
  const [autoCompleteResponse, setAutoCompleteResponse] = useState('');
  const autocompleteKey = useRef(new Date().getTime().toString());
  const [alreadySelectedError, setAlreadySelectedError] = useState(false);
  const idx = actionData
    .map((action) => action.type)
    .indexOf('Dynamic_Group_Assignment');
  const actionAtIndex = actionData[idx];
  if (!isDynamicGroupAssignment(actionAtIndex)) {
    return null;
  }

  const currentAction: DynamicGroupAssignmentActionType = actionAtIndex;
  const error: FieldErrors<DynamicGroupAssignmentActionType> | undefined =
    errors?.actionData?.[idx];

  const dynamicGroupAssignment = currentAction.dynamicGroupAssignment;

  const profileFieldValue = dynamicGroupAssignment.profileField;
  const regExValue = dynamicGroupAssignment.regEx;

  const removeSelection = (email: string, index: number) => {
    const newList =
      currentAction.dynamicGroupAssignment.groupEmail.filter(
        (groupEmail: string) => groupEmail !== email
      ) ?? [];
    const newRoles =
      currentAction.dynamicGroupAssignment.groupRole.filter(
        (_, i) => i !== index
      ) ?? [];
    setValue(
      `actionData.${idx}.dynamicGroupAssignment.groupEmail`,
      [...newList],
      { shouldValidate: true }
    );
    setValue(`actionData.${idx}.dynamicGroupAssignment.groupRole`, newRoles);
  };

  const memberRoleOptions = [
    { displayValue: 'Member', value: 'MEMBER' },
    { displayValue: 'Manager', value: 'MANAGER' },
    { displayValue: 'Owner', value: 'OWNER' },
  ];

  const handleProfileFieldTypeChange = (
    newValue: { display: string; value: string } | null
  ) => {
    setValue(
      `actionData.${idx}.dynamicGroupAssignment.profileFieldType`,
      newValue?.value || ''
    );
    trigger(`actionData.${idx}.dynamicGroupAssignment.profileFieldType`);
  };

  const handleProfileFieldChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setValue(
      `actionData.${idx}.dynamicGroupAssignment.profileField`,
      event.target.value
    );
    trigger(`actionData.${idx}.dynamicGroupAssignment.profileField`);
  };
  const handleRegExChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(
      `actionData.${idx}.dynamicGroupAssignment.regEx`,
      event.target.value
    );
    trigger(`actionData.${idx}.dynamicGroupAssignment.profileField`);
  };
  const handleExactChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(
      `actionData.${idx}.dynamicGroupAssignment.exact`,
      event.target.checked
    );
  };

  return (
    <Box width={'100%'}>
      <Stack
        width={'100%'}
        display={'flex'}
        flexDirection={'row'}
        gap={1}
        sx={{ p: 1 }}
      >
        <Controller
          name={`actionData.${idx}.dynamicGroupAssignment.profileFieldType`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              {...field}
              value={
                profileFieldOptions.find(
                  (option) => option.value === field.value
                ) || null
              }
              onChange={(_event, newValue) => {
                handleProfileFieldTypeChange(newValue);
              }}
              disablePortal
              options={profileFieldOptions}
              sx={{ width: 340 }}
              getOptionLabel={(option) => option.display}
              isOptionEqualToValue={(option, value) =>
                option.value === value?.value
              }
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.display}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Profile Field"
                  error={!!error}
                  helperText={error?.message || ' '}
                />
              )}
            />
          )}
        />

        <Stack display={'flex'} flexDirection={'column'} gap={1}>
          <Controller
            name={`actionData.${idx}.dynamicGroupAssignment.profileField`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                onChange={handleProfileFieldChange}
                value={field.value || ''}
                label="Field Value"
                variant="outlined"
                error={!!error}
                sx={{ width: 340 }}
                disabled={!!regExValue}
                helperText={error?.message || ' '}
              />
            )}
          />
          <Controller
            name={`actionData.${idx}.dynamicGroupAssignment.regEx`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Tooltip
                arrow
                title="A regular expression is a sequence of characters that defines a search pattern. For example, to match values starting with 'Phil', use the regex `^Phil`."
              >
                <TextField
                  {...field}
                  label="Regular Expression"
                  onChange={handleRegExChange}
                  variant="outlined"
                  sx={{ mb: 1 }}
                  disabled={
                    !!profileFieldValue || !!dynamicGroupAssignment.exact
                  }
                  value={regExValue || ''}
                  error={!!error}
                />
              </Tooltip>
            )}
          />
        </Stack>

        <FormGroup>
          <Tooltip
            arrow
            title="When checked, the Field Value must be a case-sensitive match."
          >
            <FormControlLabel
              control={
                <Checkbox
                  onChange={handleExactChange}
                  checked={dynamicGroupAssignment.exact}
                  name="exact"
                  disabled={!!regExValue}
                />
              }
              label="Exact"
            />
          </Tooltip>
        </FormGroup>
      </Stack>
      <Stack sx={{ p: 1 }}>
        <Stack gap={1}>
          <Stack flexDirection={'row'} gap={1}>
            <Box sx={{ width: 340 }}>
              <Controller
                name={`actionData.${idx}.dynamicGroupAssignment.groupEmail`}
                control={control}
                render={() => (
                  <GroupsAutocomplete
                    newKey={`${autocompleteKey.current}`}
                    autocompleteValue={autoCompleteResponse}
                    setAutoCompleteValue={(value) => {
                      if (!value) {
                        setAlreadySelectedError(false);
                        return;
                      }
                      if (
                        currentAction.dynamicGroupAssignment.groupEmail.includes(
                          value
                        )
                      ) {
                        setAlreadySelectedError(true);
                      } else {
                        setAlreadySelectedError(false);
                        const currentEmails =
                          currentAction.dynamicGroupAssignment.groupEmail || [];
                        const currentRoles =
                          currentAction.dynamicGroupAssignment.groupRole || [];

                        setValue(
                          `actionData.${idx}.dynamicGroupAssignment.groupEmail`,
                          [...currentEmails, value]
                        );

                        setValue(
                          `actionData.${idx}.dynamicGroupAssignment.groupRole`,
                          [...currentRoles, 'MEMBER']
                        );

                        autocompleteKey.current = new Date()
                          .getTime()
                          .toString();
                        setAutoCompleteResponse('');
                        trigger(
                          `actionData.${idx}.dynamicGroupAssignment.groupEmail`
                        );
                      }
                    }}
                    errors={
                      !!error?.dynamicGroupAssignment?.groupEmail ||
                      alreadySelectedError
                    }
                    helperText={
                      alreadySelectedError
                        ? 'Group is already selected.'
                        : error?.dynamicGroupAssignment?.groupEmail
                        ? error?.dynamicGroupAssignment?.groupEmail?.message
                        : ' '
                    }
                  />
                )}
              />
            </Box>
          </Stack>
        </Stack>
        <Box sx={{ maxHeight: 200 }}>
          <Paper sx={{ minWidth: 470, overflow: 'hidden', maxHeight: 200 }}>
            <TableContainer sx={{ minWidth: 470, maxHeight: 200 }}>
              <Table size={'small'} stickyHeader aria-label="searched-groups">
                <TableHead>
                  <TableRow>
                    <TableCell>Selected Groups</TableCell>
                    <TableCell>Group Role</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentAction.dynamicGroupAssignment.groupEmail
                    ? currentAction.dynamicGroupAssignment.groupEmail.map(
                        (group, index) => (
                          <TableRow key={group}>
                            <TableCell>
                              <Stack
                                flexDirection="row"
                                alignItems="center"
                                justifyContent="space-between"
                              >
                                <Typography variant="body2">{group}</Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Stack
                                flexDirection="row"
                                alignItems="center"
                                justifyContent="space-between"
                              >
                                <TextField
                                  inputProps={{
                                    id: 'memberRole',
                                  }}
                                  InputLabelProps={{
                                    htmlFor: 'memberRole',
                                  }}
                                  label="Member Role"
                                  value={
                                    currentAction.dynamicGroupAssignment
                                      ?.groupRole![index]
                                  }
                                  onChange={(event) => {
                                    const newRoles = [
                                      ...(currentAction.dynamicGroupAssignment
                                        ?.groupRole || []),
                                    ];
                                    newRoles[index] = event.target.value;
                                    setValue(
                                      `actionData.${idx}.dynamicGroupAssignment.groupRole`,
                                      newRoles
                                    );
                                  }}
                                  autoComplete="off"
                                  select
                                  sx={{ width: 115 }}
                                >
                                  {memberRoleOptions.map((option) => (
                                    <MenuItem
                                      value={option.value}
                                      key={option.displayValue}
                                    >
                                      {option.displayValue}
                                    </MenuItem>
                                  ))}
                                </TextField>
                                <Tooltip arrow title="Delete">
                                  <span>
                                    <IconButton
                                      onClick={() =>
                                        removeSelection(group, index)
                                      }
                                    >
                                      <Delete />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        )
                      )
                    : null}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
}
